import axios from "axios";
import ObservatoryService from "@/services/observatory.service";
import SynodicCalculationService from "@/services/synodic-calculation.service";
import VisualisationService from "@/services/visualisation.service";
import UtilsService from "@/services/utils.service";
import store from "@/store";
import CommonActions from "@/store/common/common-actions";
import CommonGetters from "@/store/common/common-getters";
import Config from "@/constants/config";
import SpacekitService from "./spacekit.service";

const SyntheticObjectsService = {
    SCD_CUSTOM_OBSERVATORY_ENABLED: false,
    SYNTHETIC_OBJECT_SUFFIX: "_synthetic-object",
    LD_TO_AU: 0.0025695686589742,
    LD_LIMIT: 3,
    FLYBY_LIMITS: { // Sphere Of Influence (SOI) source: https://en.wikipedia.org/wiki/Sphere_of_influence_(astrodynamics)
        mercury: 0.000782, // au
        venus: 0.0041177, // au
        earth: 0.00770863, // au
        mars: 0.0038637, // au
        jupiter: 0.322197, // au
        saturn: 0.3643099, // au
        uranus: 0.34693, // au
        neptune: 0.57621141, // au
    },
    OBJECT_TYPES: {
        fvt: 'FVT',
        elliptical: 'ELLIPTICAL',
        hyperbolic: 'HYPERBOLIC',
        scd: 'DEFAULT',
        default: 'DEFAULT',
    },
    isToolWithSyntheticObjects() {
        const tools = SyntheticObjectsService.getToolsWithSyntheticObjects();
        const currentTool = VisualisationService.getTool();
        return tools.length && tools.includes(currentTool);
    },

    isDefaultSyntheticObjects() {
        const currentTool = VisualisationService.getTool();
        return currentTool !== "fvt";
    },

    getToolsWithSyntheticObjects() {
        const config = SyntheticObjectsService.getSyntheticObjectsConfig();
        return config.availableTools;
    },

    getDefaultSyntheticInputsList() {
        const config = SyntheticObjectsService.getSyntheticObjectsConfig();
        return config.parametersInputs.default;
    },

    getFvtSyntheticInputsList() {
        const config = SyntheticObjectsService.getSyntheticObjectsConfig();
        return config.parametersInputs.fvt;
    },

    getEllipticalInputsList() {
        const config = SyntheticObjectsService.getSyntheticObjectsConfig();
        return config.parametersInputs.default;
    },

    getHyperbolicInputsList() {
        const config = SyntheticObjectsService.getSyntheticObjectsConfig();
        return config.parametersInputs.hyperbolic;
    },

    getSyntheticObjectsList(objectType = null) {
        let objectsList = [];
        if (objectType === null) {
            switch (VisualisationService.getTool()) {
                case "fvt":
                    objectsList = SyntheticObjectsService.getSyntheticFvtObjectsList();
                    break
                case "scd":
                    objectsList = SyntheticObjectsService.getSyntheticDefaultObjectsList();
                    break
                case "ovt":
                case "sovt": {
                    const ellipticalObjectsList = SyntheticObjectsService.getEllipticalObjectsList();
                    const hyperbolicObjectsList = SyntheticObjectsService.getHyperbolicObjectsList();
                    objectsList = [...ellipticalObjectsList, ...hyperbolicObjectsList]
                    break
                }
                default:
                    objectsList = SyntheticObjectsService.getSyntheticDefaultObjectsList();
                    break
            }
        } else {
            switch (objectType) {
                case SyntheticObjectsService.OBJECT_TYPES.elliptical:
                    objectsList = SyntheticObjectsService.getEllipticalObjectsList();
                    break
                case SyntheticObjectsService.OBJECT_TYPES.hyperbolic:
                    objectsList = SyntheticObjectsService.getHyperbolicObjectsList();
                    break
                case SyntheticObjectsService.OBJECT_TYPES.fvt:
                    objectsList = SyntheticObjectsService.getSyntheticFvtObjectsList();
                    break
                default:
                    objectsList = SyntheticObjectsService.getSyntheticDefaultObjectsList()
            }
        }
        return objectsList;
    },

    getSyntheticDefaultObjectsList() {
        return store.getters[CommonGetters.syntheticDefaultObjectsList];
    },

    getSyntheticFvtObjectsList() {
        return store.getters[CommonGetters.syntheticFvtObjectsList];
    },

    getEllipticalObjectsList() {
        return store.getters[CommonGetters.ellipticalObjectsList];
    },

    getHyperbolicObjectsList() {
        return store.getters[CommonGetters.hyperbolicObjectsList];
    },

    getSyntheticObjectName(syntheticObject) {
        return syntheticObject.objectName;
    },

    setSyntheticDefaultObjectsList(objectsList) {
        store.dispatch(
            CommonActions.setSyntheticDefaultObjectsList,
            objectsList
        );
    },

    setEllipticalObjectsList(objectsList) {
        store.dispatch(CommonActions.setEllipticalObjectsList, objectsList);
    },

    setHyperbolicObjectsList(objectsList) {
        store.dispatch(CommonActions.setHyperbolicObjectsList, objectsList);
    },

    setSyntheticFvtObjectsList(objectsList) {
        store.dispatch(CommonActions.setSyntheticFvtObjectsList, objectsList);
    },

    isSyntheticObject(objectDesignator, objectType = null) {
        const objectsList = SyntheticObjectsService.getSyntheticObjectsList(objectType);
        return (
            UtilsService.findItemIndexInObjectList(
                "designator",
                objectDesignator,
                objectsList
            ) !== -1
        );
    },

    getSyntheticObjectDataByDesignator(designator, objectType = null) {
        const objectsList = SyntheticObjectsService.getSyntheticObjectsList(objectType);
        return UtilsService.findItemInObjectList(
            "designator",
            designator,
            objectsList
        );
    },

    getObjectDesignator(realObject) {
        return (
            realObject.number ? realObject.number : realObject.name
        ).toString();
    },

    async getRealObjectParameters(designator) {
        if (!designator) {
            return;
        }
        const apiUrl = SynodicCalculationService.getEphemeridesUrl(designator);
        const loadingObjects =
            SynodicCalculationService.loadingObjectsEphemerides();
        loadingObjects.push(designator);
        const response = await axios(apiUrl);
        return response.data.ephemerides;
    },

    getUpdatedObjectParameters(objectParameters) {
        const parameters = {};
        const updatedObjectParamters =
            SyntheticObjectsService.setMissingParamters(objectParameters);
        const parametersMap = SyntheticObjectsService.getObjectParametersMap(objectParameters && objectParameters.type ? objectParameters.type : null);
        Object.keys(parametersMap).map(
            (key) =>
                (parameters[parametersMap[key]] = updatedObjectParamters[key])
        );
        return parameters;
    },

    setMissingParamters(objectParameters) {
        !objectParameters.g && (objectParameters.g = 0.25);
        if (
            !objectParameters.aphelion &&
            objectParameters.a &&
            objectParameters.e
        ) {
            objectParameters.aphelion =
                objectParameters.a * (1 + objectParameters.e);
        }
        return objectParameters;
    },

    isUserDefinedObjectCustomObservatoryEnabled() {
        if (SyntheticObjectsService.SCD_CUSTOM_OBSERVATORY_ENABLED) {
            return true;
        }
        const isCustomObservatory =
            ObservatoryService.getSelectedObservatory().code === null;
        const isUserDefinedObject =
            !!SyntheticObjectsService.getSyntheticObjectsList().length;
        return !(isCustomObservatory && isUserDefinedObject);
    },

    createSyntheticObjectDesignator(objectName) {
        return `${objectName.replace(/[^\w\s-]/g, "").replace(/[-\s]+/g, "_")}${
            SyntheticObjectsService.SYNTHETIC_OBJECT_SUFFIX
        }`;
    },

    getSyntheticObjectParameters(designator, objectType = null) {
        const syntheticObjectData =
            SyntheticObjectsService.getSyntheticObjectDataByDesignator(
                designator, objectType
            );
        if (!syntheticObjectData) {
            return;
        }
        const syntheticParameters = {};
        const parametersMap = SyntheticObjectsService.getObjectParametersMap(syntheticObjectData.type ? syntheticObjectData.type : objectType);
        const parametersNames = Object.values(parametersMap);
        const parametersKeys = Object.keys(parametersMap);
        Object.keys(syntheticObjectData).forEach((parameterName) => {
            const parameterIndex = parametersNames.indexOf(parameterName);
            if (parameterIndex === -1) {
                syntheticParameters[parameterName] =
                    syntheticObjectData[parameterName];
                return;
            }
            syntheticParameters[parametersKeys[parameterIndex]] =
                syntheticObjectData[parameterName];
        });
        if (syntheticObjectData.type === SyntheticObjectsService.OBJECT_TYPES.hyperbolic || objectType === SyntheticObjectsService.OBJECT_TYPES.hyperbolic) {
            syntheticParameters["e"] = syntheticParameters["eccentricity"]
        } else {
            const aphelion = syntheticParameters["aphelion"] ? syntheticParameters["aphelion"]: 0
            syntheticParameters["a"] = (syntheticParameters["q"] + aphelion) / 2;
            syntheticParameters["e"] = aphelion / syntheticParameters["a"] - 1;
        }
        syntheticParameters["object"] = "asteroid";
        return syntheticParameters;
    },

    checkPerturbedSyntheticObjects() {
        const syntheticObjectsList =
            SyntheticObjectsService.getSyntheticObjectsList();
        if (!syntheticObjectsList.length) {
            return;
        }
        const checkedObjectsList = VisualisationService.getIsChecked();
        const perturbedOrbitObjectsList = VisualisationService.getIsPerturbed();
        syntheticObjectsList.forEach(async (object) => {
            const isShowedObject = checkedObjectsList.includes(
                object.designator
            );
            const isPerturbed = perturbedOrbitObjectsList.includes(
                object.designator
            );
            if (!isPerturbed || !isShowedObject) {
                return;
            }
            await VisualisationService.addObject(object.designator, true);
        });
    },

    getSyntheticObjectApiParameters(syntheticObjectData, objectType = null) {
        const now = new Date();
        const currentUtcMonth = String(now.getUTCMonth() + 1).padStart(2, "0");
        const currentUtcDay = String(now.getUTCDate()).padStart(2, "0");
        const perturbedOrbitStartDate = "1959-01-01T00:00Z";
        const perturbedOrbitEndDate = `${now.getUTCFullYear() + 100}-${currentUtcMonth}-${currentUtcDay}T00:00Z`;
        if (objectType === SyntheticObjectsService.OBJECT_TYPES.hyperbolic || syntheticObjectData.type === SyntheticObjectsService.OBJECT_TYPES.hyperbolic) {
            return {
                desig: syntheticObjectData.designator,
                initD: perturbedOrbitStartDate,
                finalD: perturbedOrbitEndDate,
                timeStep: 1,
                timeStepUnit: 'days',
                com_epoch: syntheticObjectData.epoch,
                com_q: syntheticObjectData.q,
                com_e: syntheticObjectData.e,
                com_i: syntheticObjectData.i,
                com_long: syntheticObjectData.om,
                com_omega: syntheticObjectData.w,
                com_tp: syntheticObjectData.tp,
            }
        } else {
            return {
                name: syntheticObjectData.designator,
                utcStart: perturbedOrbitStartDate,
                utcEnd: perturbedOrbitEndDate,
                step: 1,
                kep_epoch: syntheticObjectData.epoch,
                kep_a: syntheticObjectData.a,
                kep_e: syntheticObjectData.e,
                kep_i: syntheticObjectData.i,
                kep_long: syntheticObjectData.om,
                kep_omega: syntheticObjectData.w,
                kep_mean: syntheticObjectData.ma,
            };
        }
    },

    getSyntheticObjectSovtApiParameters(designator, objectType = null) {
        const { startDateString, endDateString } =
            SynodicCalculationService.getStringTimeframe();
        const defaultParameters = {
            desig: designator,
            initD: startDateString,
            finalD: endDateString,
            planet: true,
            timeStep: 1,
            timeStepUnit: "days",
        };
        const isSyntheticObject = SyntheticObjectsService.isSyntheticObject(designator, objectType);
        if (!isSyntheticObject) {
            return defaultParameters;
        }
        const syntheticObjectData = SyntheticObjectsService.getSyntheticObjectParameters(designator, objectType);
        let syntheticObjectParameters = {
            kep: true,
        }
        if (objectType === SyntheticObjectsService.OBJECT_TYPES.hyperbolic || syntheticObjectData.type === SyntheticObjectsService.OBJECT_TYPES.hyperbolic) {
            syntheticObjectParameters = Object.assign(syntheticObjectParameters, {
                com_epoch: syntheticObjectData.epoch,
                com_q: syntheticObjectData.q,
                com_e: syntheticObjectData.e,
                com_i: syntheticObjectData.i,
                com_long: syntheticObjectData.om,
                com_omega: syntheticObjectData.w,
                com_tp: syntheticObjectData.tp,
            })
        } else {
            syntheticObjectParameters = Object.assign(syntheticObjectParameters, {
                kep_epoch: syntheticObjectData.epoch,
                kep_a: syntheticObjectData.a,
                kep_e: syntheticObjectData.e,
                kep_i: syntheticObjectData.i,
                kep_long: syntheticObjectData.om,
                kep_omega: syntheticObjectData.w,
                kep_mean: syntheticObjectData.ma,
            })
        }
        return { ...defaultParameters, ...syntheticObjectParameters };
    },

    getSyntheticObjectFvtApiParameters() {
        const syntheticObjectData = SyntheticObjectsService.getSyntheticFvtObjectsList()[0];
        if (!syntheticObjectData) return;

        const center = SyntheticObjectsService.getSyntheticMainBody().id;

        // Convert peri distance [au] to Lunar Distances for a simple scale
        const periAU = syntheticObjectData.pericentreDistance;
        const periLD = periAU / SyntheticObjectsService.LD_TO_AU;

        // Adaptive sampling near closest approach (days)
        let stepDays, ephLenDays = 10;
        if (periLD <= 0.03113363) {           // 0.03113363 LD = 0.00008 AU
            stepDays = 1 / (24 * 60);         // 1 minute
        } else if (periLD <= 0.0389170376) {  // 0.0389170376 LD= 0.0001 AU
            stepDays = 5 / (24 * 60);         // 5 minutes
        } else {
            stepDays = 1 / 24;                // 1 hour (default)
        }
  
        return {
            name: syntheticObjectData.designator,
            step: stepDays,                  // finer timestep near CA
            flyby_maxdist: SyntheticObjectsService.LD_LIMIT,
            eph_len: ephLenDays,             // narrower window when very close
            com_epoch: syntheticObjectData.pericentrePassage,
            com_q: syntheticObjectData.pericentreDistance * SpacekitService.AU, //com_q for s.m.a. (float in km) = Pericenter distance
            com_e: syntheticObjectData.eccentricity,
            com_i: syntheticObjectData.inclination,
            com_long: syntheticObjectData.ascendingNodeLongitude,
            com_omega: syntheticObjectData.pericentreArgument,
            com_tp: syntheticObjectData.pericentrePassage,
            center,
        };
    },

    getOvtObjectsInformationsByDesignator(designator) {
        const checkedObjectsList = VisualisationService.getIsChecked();
        const selectedObjectDesignator =
            VisualisationService.getSelectedObjectName();
        const perturbedObjectsList = VisualisationService.getIsPerturbed();
        const isObjectChecked = checkedObjectsList.includes(designator);
        const isObjectSelected = selectedObjectDesignator === designator;
        const isObjectPerturbed = perturbedObjectsList.includes(designator);
        return {
            checkedObjectsList,
            perturbedObjectsList,
            isObjectChecked,
            isObjectSelected,
            isObjectPerturbed,
        };
    },

    checkIsSyntheticObjectFlybyCalculated() {
        const syntheticObjectsList = SyntheticObjectsService.getSyntheticFvtObjectsList();
        if (!syntheticObjectsList.length) {
            return false;
        }
        const syntheticObjectData = syntheticObjectsList[0];
        const selectedObjectData = VisualisationService.getSelectedCloseApproach();
        if (!selectedObjectData || !selectedObjectData.objectName) {
            return false;
        }
        // eslint-disable-next-line
        const {jds, mainBody, ...selectedObjectParameters} = selectedObjectData;
        const parameters = Object.keys(syntheticObjectData);
        return !parameters.some(parameter => syntheticObjectData[parameter] !== selectedObjectParameters[parameter]);
    },

    checkIsNonSyntheticObjectFlybyCalculated() {
        const selectedObjectData = VisualisationService.getSelectedCloseApproach();
        return selectedObjectData && !selectedObjectData.objectName;
    },

    checkIsObjectOnList(syntheticObjectsList, objectDesignator) {
        return syntheticObjectsList.some(
            (asteroid) => asteroid.designator === objectDesignator
        );
    },

    getOvtSyntheticObjectPerturbedOrbitApiUrl(objectType = null) {
        const api = (objectType === SyntheticObjectsService.OBJECT_TYPES.hyperbolic) ? Config.api.getOvtHyperbolicObjectPerturbed: Config.api.getOvtSyntheticObjectPerturbed;
        return Config.api.restUrl + api;
    },

    getFvtSyntheticObjectApiUrl() {
        return Config.api.restUrl + Config.api.closeApproachesSyntheticObject;
    },

    setSyntheticMainBody(object) {
        store.dispatch(CommonActions.setSyntheticMainBody, object);
    },

    getSyntheticMainBody() {
        return store.getters[CommonGetters.syntheticMainBody];
    },

    getObjectParametersMap(objectType = null) {
        if (objectType === SyntheticObjectsService.OBJECT_TYPES.hyperbolic) {
            return {
                epoch: "referenceEpoch",
                q: "perihelionDistance",
                i: "inclination",
                om: "ascendingNodeLongitude",
                w: "perihelionArgument",
                tp: "pericentrePassage",
                mag: "absoluteMagnitude",
                g: "slopeParameter",
            };
        } else {
            return {
                epoch: "referenceEpoch",
                q: "perihelionDistance",
                aphelion: "aphelionDistance",
                i: "inclination",
                om: "rightAscension",
                w: "perihelionArgument",
                ma: "meanAnomaly",
                mag: "absoluteMagnitude",
                g: "slopeParameter",
            };
        }
    },

    getObjectParametersUnitLabelMap() {
        return {
            perihelionDistance: "au",
            aphelionDistance: "au",
            inclination: "degrees",
            rightAscension: "degrees",
            perihelionArgument: "degrees",
            meanAnomaly: "degrees",
            absoluteMagnitude: "mag",
        };
    },

    checkIsFlybyAboveLimit() {
        const syntheticObjectData = SyntheticObjectsService.getSyntheticFvtObjectsList()[0];
        const mainBody = SyntheticObjectsService.getSyntheticMainBody().id;
        const limit = SyntheticObjectsService.FLYBY_LIMITS[mainBody];
        if (!syntheticObjectData) {
            return false;
        }
        const {pericentreDistance} = syntheticObjectData;
        return pericentreDistance > limit;
    },

    setIsSyntheticObjectFlybyAboveLdLimit(isAboveLimit) {
        store.dispatch(CommonActions.setIsSyntheticObjectFlybyAboveLdLimit, isAboveLimit);
    },

    getIsSyntheticObjectFlybyAboveLdLimit() {
        return store.getters[CommonGetters.isSyntheticObjectFlybyAboveLdLimit];
    },

    getSyntheticObjectsConfig() {
        return Config.syntheticObjectsConfiguration;
    },
};

export default SyntheticObjectsService;
